import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  getAllDocuments,
  deleteDocument,
} from "../../../api/hrm/documents/info";
import { getDocumentGroups } from "../../../api/hrm/documents/groups";
import Filter from "./DocumentFilter";
import CreateModal from "./DocumentCreateModal";
import DocumentDetailModal from "./DocumentDetailModal";
import DocumentGroupSidebar from "./DocumentGroupSidebar";
import {
  Trash2,
  Sparkles,
  Folder,
  FileText,
  User,
  Calendar,
  Plus,
  ChevronRight,
} from "lucide-react";
import {
  showConfirmDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";
import { startHrmDocumentsTour } from "./documentsTour";
import { formatDateVN, formatDateTimeVN } from "../../../utils/formatUtils";

const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  if (bytes >= 1_000_000_000) return (bytes / 1_000_000_000).toFixed(2) + " GB";
  if (bytes >= 1_000_000) return (bytes / 1_000_000).toFixed(2) + " MB";
  if (bytes >= 1_000) return (bytes / 1_000).toFixed(2) + " KB";
  return bytes + " B";
};

const formatFileType = (fileType, fileName = "") => {
  if (fileType) {
    const raw = String(fileType).toLowerCase().trim();
    if (raw.includes("pdf")) return "PDF";
    if (raw.includes("word") || raw.includes("officedocument.wordprocessingml") || raw.includes("msword")) return "DOC";
    if (raw.includes("sheet") || raw.includes("officedocument.spreadsheetml") || raw.includes("excel")) return "XLS";
    if (raw.includes("presentation") || raw.includes("officedocument.presentationml") || raw.includes("powerpoint")) return "PPT";
    if (raw.includes("zip") || raw.includes("compressed") || raw.includes("tar") || raw.includes("archive")) return "ZIP";
    if (raw.includes("rar")) return "RAR";
    if (raw.includes("png")) return "PNG";
    if (raw.includes("jpeg") || raw.includes("jpg")) return "JPG";
    if (raw.includes("svg")) return "SVG";
    if (raw.includes("image")) return "IMG";
    if (raw.includes("csv")) return "CSV";
    if (raw.includes("txt") || raw.includes("plain")) return "TXT";
    if (raw.includes("/")) {
      const sub = raw.split("/")[1]?.replace(/^x-/, "");
      if (sub && sub.length <= 4) return sub.toUpperCase();
    }
    if (raw.length <= 4) return raw.toUpperCase();
  }
  if (fileName && fileName.includes(".")) {
    const ext = fileName.split(".").pop().trim().toLowerCase();
    if (ext && ext.length <= 5) return ext.toUpperCase();
  }
  return "FILE";
};

const STATUS_BADGE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-amber-50 text-amber-800 border-amber-200",
  inactive: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_LABEL = {
  active: "Hiệu lực",
  expired: "Hết hiệu lực",
  inactive: "Ngừng dùng",
};

const DocumentCardPage = () => {
  const [documents, setDocuments] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const [filterValues, setFilterValues] = useState({
    keyword: "",
    group_id: "",
    status: "",
    from_date: "",
    to_date: "",
  });
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [groupStats, setGroupStats] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [mobileTreeCollapsed, setMobileTreeCollapsed] = useState(true);
  const latestReqIdRef = useRef(0);
  const filterValuesRef = useRef(filterValues);
  filterValuesRef.current = filterValues;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const res = await getDocumentGroups();
      const items = Array.isArray(res) ? res : res?.items || res?.data || [];
      setAllGroups(items);
    } catch {
      setAllGroups([]);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const reloadDocuments = async ({
    nextFilters = filterValues,
    nextPage = page,
    nextPageSize = pageSize,
  } = {}) => {
    let reqId = 0;
    try {
      reqId = Date.now();
      latestReqIdRef.current = reqId;
      setLoading(true);
      const payload = Object.fromEntries(
        Object.entries(nextFilters || {}).filter(
          ([, v]) => v !== "" && v !== null && v !== undefined
        )
      );
      payload.page = nextPage;
      payload.page_size = nextPageSize;
      payload.include_group_stats = true;
      const data = await getAllDocuments(payload);
      if (latestReqIdRef.current !== reqId) return;
      if (Array.isArray(data)) {
        setDocuments(data);
        setTotal(data.length);
        setTotalPages(1);
        setGroupStats([]);
      } else {
        setDocuments(data?.items || []);
        setTotal(Number(data?.total || 0));
        setTotalPages(Number(data?.total_pages || 1));
        setGroupStats(Array.isArray(data?.group_stats) ? data.group_stats : []);
      }
    } catch (err) {
      console.error("Lỗi khi tải tài liệu:", err);
      showErrorToast("Không thể tải danh sách tài liệu");
    } finally {
      if (latestReqIdRef.current === reqId) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    reloadDocuments({
      nextFilters: filterValues,
      nextPage: page,
      nextPageSize: pageSize,
    });
  }, [filterValues, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const treeGroups = useMemo(() => {
    const normParent = (v) =>
      v === null || v === undefined || v === "" || v === "null"
        ? null
        : String(v);
    const countMap = new Map(
      groupStats.map((s) => [String(s.group_id || ""), Number(s.total || 0)])
    );
    const fromApi = allGroups.map((g) => {
      const id = String(g.id || g.group_id || "");
      return {
        id,
        group_id: id,
        group_name: g.group_name,
        description: g.description || "",
        parent_id: normParent(g.parent_id),
        sort_index: g.sort_index ?? 0,
        total: countMap.has(id) ? countMap.get(id) : 0,
      };
    });
    groupStats.forEach((s) => {
      const id = String(s.group_id || "");
      if (id && !fromApi.some((g) => g.id === id)) {
        fromApi.push({
          id,
          group_id: id,
          group_name: s.group_name || "Nhóm khác",
          parent_id: normParent(s.parent_id),
          sort_index: 0,
          total: Number(s.total || 0),
        });
      }
    });
    // Roll-up: tổng folder = tài liệu gán trực tiếp + mọi phân loại con
    const withRollup = fromApi.map((g) => {
      if (g.parent_id) return g;
      const childSum = fromApi
        .filter((c) => c.parent_id === g.id)
        .reduce((sum, c) => sum + Number(c.total || 0), 0);
      return { ...g, total: Number(g.total || 0) + childSum };
    });
    return withRollup.filter((g) => g.id);
  }, [allGroups, groupStats]);

  const totalAllFromStats = useMemo(
    () => groupStats.reduce((sum, item) => sum + Number(item.total || 0), 0),
    [groupStats]
  );
  // Khi đang lọc theo nhóm, chip "Tất cả" nên vẫn hiện tổng toàn cục từ stats không lọc group.
  // group_stats BE được tính không gồm group_id filter — OK dùng totalAllFromStats.
  const displayAllTotal =
    !filterValues.group_id && total ? total : totalAllFromStats || total;

  const selectedGroupName = useMemo(() => {
    if (!filterValues.group_id) return "Tất cả tài liệu";
    const g = treeGroups.find((x) => x.id === filterValues.group_id);
    if (!g) return "Nhóm tài liệu";
    if (g.parent_id) {
      const parent = treeGroups.find((x) => x.id === g.parent_id);
      return parent
        ? `${parent.group_name} / ${g.group_name}`
        : g.group_name;
    }
    return g.group_name;
  }, [filterValues.group_id, treeGroups]);

  const handleFilterChange = (nextFilters) => {
    // Giữ group_id từ cây thư mục — Filter không được ghi đè khi clear trừ khi explicit
    const merged = {
      ...nextFilters,
      group_id:
        nextFilters.group_id !== undefined
          ? nextFilters.group_id
          : filterValuesRef.current.group_id,
    };
    setPage(1);
    setFilterValues(merged);
  };

  const handleGroupSelect = (groupId) => {
    setPage(1);
    setFilterValues((prev) => ({ ...prev, group_id: groupId || "" }));
    if (isMobile) setMobileTreeCollapsed(true);
  };

  const handleDelete = (doc) => {
    showConfirmDialog({
      title: "Xác nhận xóa",
      text: `Bạn có chắc muốn xóa tài liệu "${doc.file_name}" không?`,
      confirmText: "Xóa",
    }).then((result) => {
      if (!result.isConfirmed) return;
      deleteDocument(doc.id)
        .then((res) => {
          showSuccessToast(res.message || "Đã xóa tài liệu!");
          reloadDocuments({
            nextFilters: filterValues,
            nextPage: page,
            nextPageSize: pageSize,
          });
          loadGroups();
        })
        .catch((err) => {
          console.error("❌ Lỗi khi xóa tài liệu:", err);
          showErrorToast("Không thể xóa tài liệu, vui lòng thử lại.");
        });
    });
  };

  return (
    <div className="w-full min-h-full bg-gray-50/40 p-3 pb-24 md:p-6 md:pb-8">
      {/* HEADER */}
      <div
        className="mb-4 sm:mb-5 flex items-center justify-between gap-2 sm:gap-3"
        data-tour="hrm-documents-header"
      >
        <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
          <div className="bg-red-700 p-2 sm:p-2.5 rounded-xl sm:rounded-2xl text-white shadow-lg shadow-red-200 shrink-0">
            <Folder className="h-5 w-5 sm:h-6 sm:w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-black text-gray-800 tracking-tight uppercase truncate">
              Thư viện <span className="text-red-700">tài liệu nội bộ</span>
            </h1>
            <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider truncate">
                <span className="text-red-600">{total}</span> tài liệu
                {filterValues.group_id ? (
                  <>
                    {" "}
                    trong <span className="text-gray-700">{selectedGroupName}</span>
                  </>
                ) : (
                  " đang hiển thị"
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <button
            type="button"
            onClick={() => window.setTimeout(() => startHrmDocumentsTour(), 80)}
            title="Hướng dẫn nhanh"
            aria-label="Hướng dẫn nhanh"
            className="group relative inline-flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-xl sm:rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300 shrink-0"
          >
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.2} />
            <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
              Hướng dẫn nhanh
            </span>
          </button>
        </div>
      </div>

      {/* LAYOUT: sidebar cây thư mục (chỉ hiện trên desktop >= lg) | nội dung */}
      <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 lg:gap-5 items-stretch">
        <div className="hidden lg:block w-[280px] xl:w-[300px] shrink-0">
          <DocumentGroupSidebar
            groups={treeGroups}
            selectedId={filterValues.group_id || ""}
            totalAll={displayAllTotal}
            onSelect={handleGroupSelect}
            onGroupsChanged={() => {
              loadGroups();
              reloadDocuments({
                nextFilters: filterValuesRef.current,
                nextPage: page,
                nextPageSize: pageSize,
              });
            }}
          />
        </div>

        <div className="flex-1 min-w-0 flex flex-col">
          <div data-tour="hrm-documents-filter">
            <Filter
              groups={treeGroups}
              lockedGroupId={filterValues.group_id}
              onFilterChange={handleFilterChange}
              onNewClick={() => setOpenDrawer(true)}
              onRefresh={(nextFilters) => {
                const merged = {
                  ...(nextFilters || filterValues),
                  group_id:
                    nextFilters?.group_id !== undefined
                      ? nextFilters.group_id
                      : filterValues.group_id,
                };
                setFilterValues(merged);
                reloadDocuments({
                  nextFilters: merged,
                  nextPage: 1,
                  nextPageSize: pageSize,
                });
              }}
            />
          </div>

          <div className="mb-2.5 flex items-center gap-2 px-1">
            <Folder className="h-4 w-4 text-red-600 shrink-0" />
            <h3 className="text-sm font-bold text-gray-800 truncate">
              {selectedGroupName}
            </h3>
            <span className="text-xs text-gray-400 tabular-nums">({total})</span>
          </div>

          {loading && (
            <p className="text-gray-500 text-sm italic py-8 text-center">
              Đang tải dữ liệu...
            </p>
          )}

          {!loading && documents.length === 0 && (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 sm:p-10 text-center text-sm text-gray-400 italic">
              Không có tài liệu trong thư mục này.
            </div>
          )}

          <motion.div
            layout
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2.5 sm:gap-3"
            data-tour="hrm-documents-grid"
          >
            <AnimatePresence>
              {documents.map((doc, index) => (
                <motion.div
                  key={doc.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: Math.min(index * 0.03, 0.3) }}
                  onClick={() => setOpenDetail(doc)}
                  className="relative group cursor-pointer rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md hover:border-red-200 hover:-translate-y-0.5 transition-all overflow-hidden"
                >
                  {/* MOBILE COMPACT CARD (< sm) */}
                  <div className="sm:hidden flex items-start gap-3 p-3">
                    <div className="flex flex-col items-center justify-center h-14 w-14 rounded-xl bg-gradient-to-br from-red-50 to-gray-50 border border-red-100 shrink-0">
                      <FileText className="h-6 w-6 text-red-600" strokeWidth={1.75} />
                      <span className="text-[10px] font-bold text-gray-600 uppercase mt-0.5 tracking-wider truncate max-w-[48px] text-center">
                        {formatFileType(doc.file_type, doc.file_name)}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1.5">
                        <h3 className="font-bold text-sm text-gray-900 leading-snug line-clamp-2 group-hover:text-red-600 transition">
                          {doc.file_name}
                        </h3>
                        {doc.status ? (
                          <span
                            className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-md border ${
                              STATUS_BADGE[doc.status] || STATUS_BADGE.inactive
                            }`}
                          >
                            {STATUS_LABEL[doc.status] || doc.status}
                          </span>
                        ) : null}
                      </div>

                      {doc.description && (
                        <p className="text-xs text-gray-500 line-clamp-1 mt-1">
                          {doc.description}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1.5 text-[11px] text-gray-400">
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={11} className="shrink-0 text-red-400" />
                          {doc.issue_date ? formatDateVN(doc.issue_date) : "—"}
                        </span>
                        <span>·</span>
                        <span>{formatFileSize(doc.file_size)}</span>
                        {doc.issuer && (
                          <>
                            <span>·</span>
                            <span className="truncate max-w-[120px]">{doc.issuer}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <ChevronRight size={16} className="shrink-0 text-gray-300 self-center" />
                  </div>

                  {/* TABLET / DESKTOP CARD (>= sm) */}
                  <div className="hidden sm:flex flex-col h-full">
                    <div className="flex flex-col items-center justify-center h-20 bg-gradient-to-br from-red-50/80 to-gray-50 relative shrink-0">
                      <FileText className="h-8 w-8 text-red-600" strokeWidth={1.75} />
                      <span className="text-[10px] font-bold text-gray-500 mt-1 uppercase tracking-wider">
                        {formatFileType(doc.file_type, doc.file_name)}
                      </span>
                      {doc.status ? (
                        <span
                          className={`absolute top-2 left-2 text-[9px] font-bold px-1.5 py-0.5 rounded-md border ${
                            STATUS_BADGE[doc.status] || STATUS_BADGE.inactive
                          }`}
                        >
                          {STATUS_LABEL[doc.status] || doc.status}
                        </span>
                      ) : null}
                    </div>

                    <div className="p-3 space-y-1.5 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="font-semibold text-sm text-gray-800 line-clamp-1 group-hover:text-red-600 transition">
                          {doc.file_name}
                        </h3>
                        <p className="text-xs text-gray-500 line-clamp-2 min-h-8 mt-1">
                          {doc.description || "Không có mô tả."}
                        </p>
                      </div>
                      <div className="space-y-0.5 pt-1">
                        <p className="text-[11px] text-gray-400 truncate flex items-center gap-1">
                          <Calendar size={11} className="shrink-0 text-red-400" />
                          {doc.issuer ? `${doc.issuer} · ` : ""}
                          {doc.issue_date ? formatDateVN(doc.issue_date) : "—"}
                        </p>
                        {doc.uploader_name ? (
                          <p className="text-[11px] text-gray-400 truncate flex items-center gap-1">
                            <User size={11} className="shrink-0 text-red-400" />
                            {doc.uploader_name}
                            {doc.uploaded_at
                              ? ` · ${formatDateTimeVN(doc.uploaded_at)}`
                              : ""}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="border-t border-gray-50 px-3 py-1.5 flex justify-between items-center text-[11px] bg-gray-50/80 shrink-0">
                      <span className="text-gray-500">{formatFileSize(doc.file_size)}</span>
                      {hasPermission("hrm.documents", "D") && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(doc);
                          }}
                          className="text-red-500 hover:text-red-600 transition-transform duration-200 hover:scale-110"
                          title="Xóa tài liệu"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {!loading && totalPages > 1 && (
            <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between pt-1">
              <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                Trang <span className="font-semibold">{page}</span> /{" "}
                <span className="font-semibold">{totalPages}</span> — Tổng{" "}
                <span className="font-semibold text-red-600">{total}</span> tài liệu
              </div>
              <div className="flex items-center justify-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(Number(e.target.value));
                  }}
                  className="h-9 sm:h-10 rounded-xl border border-gray-200 px-2.5 sm:px-3 text-xs sm:text-sm bg-white"
                >
                  {[8, 12, 16, 20].map((size) => (
                    <option key={size} value={size}>
                      {size}/trang
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  className="h-9 sm:h-10 px-3.5 sm:px-4 rounded-xl border border-gray-200 bg-white text-xs sm:text-sm font-medium disabled:opacity-50 active:scale-95"
                >
                  Trước
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  className="h-9 sm:h-10 px-3.5 sm:px-4 rounded-xl border border-gray-200 bg-white text-xs sm:text-sm font-medium disabled:opacity-50 active:scale-95"
                >
                  Sau
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateModal
        isOpen={openDrawer}
        onClose={() => setOpenDrawer(false)}
        defaultGroupId={filterValues.group_id || ""}
        onSaved={() => {
          reloadDocuments({
            nextFilters: filterValues,
            nextPage: 1,
            nextPageSize: pageSize,
          });
          loadGroups();
        }}
      />

      <DocumentDetailModal
        document={openDetail}
        onClose={() => setOpenDetail(null)}
        onUpdated={() =>
          reloadDocuments({
            nextFilters: filterValues,
            nextPage: page,
            nextPageSize: pageSize,
          })
        }
      />
    </div>
  );
};

export default DocumentCardPage;
